import { IToastContextProps, ToastContext } from '@enter/core';
import React, { useState } from 'react';
import * as S from './button-bar.styles';

export interface IButtonBarProps {
    className?: string;
    changed: boolean;
    cancelLabel?: string;
    saveLabel?: string;
    saveNotice?: string;
    stopLabel?: string;
    onCancel?: () => void;
    onError?: (traceId?: string) => void;
    onSave?: () => void;
    onStop?: () => Promise<boolean>;
    useNotify?: boolean;
}

export const ButtonBar: React.FunctionComponent<IButtonBarProps> = (props: IButtonBarProps): React.ReactElement => {
    const {
        className,
        changed,
        stopLabel = 'Stop',
        cancelLabel = 'Cancel',
        saveLabel = 'Save',
        onCancel,
        onError,
        onSave,
        onStop,
        saveNotice = 'Changes saved',
        useNotify,
    } = props;
    const errorNotice = 'An error occurred!';
    const useStop = onStop && stopLabel;

    const [pending, setPending] = useState(false);
    const handleCancel = (): void => {
        setPending(true);
        onCancel && onCancel();
        setPending(false);
    };
    const handleSave = (notify: ((m: string) => void) | undefined): (() => Promise<void>) => async (): Promise<
        void
    > => {
        setPending(true);
        try {
            await (onSave && onSave());
            setPending(false);
            useNotify && notify && notify(saveNotice);
        } catch (err) {
            onError && onError();
            setPending(false);
            useNotify && notify && notify(errorNotice);
        }
    };
    const handleStop = async (): Promise<void> => {
        const stop = await (onStop && onStop());
        if (stop) {
            setPending(false);
        }
    };
    return (
        <ToastContext.Consumer>
            {(toast: IToastContextProps): React.ReactElement => (
                <S.ButtonBar className={className}>
                    <S.SaveButton
                        type="button"
                        disabled={!changed || pending}
                        variant="primary"
                        onClick={handleSave(toast.notify)}>
                        {pending ? <S.Pending size="20px" /> : saveLabel}
                    </S.SaveButton>
                    {(!useStop || !pending) && (
                        <S.CancelButton
                            type="button"
                            disabled={!changed || pending}
                            variant="outlined"
                            onClick={handleCancel}>
                            {cancelLabel}
                        </S.CancelButton>
                    )}
                    {useStop && pending && (
                        <S.CancelButton type="button" disabled={!changed} variant="outlined" onClick={handleStop}>
                            {stopLabel}
                        </S.CancelButton>
                    )}
                </S.ButtonBar>
            )}
        </ToastContext.Consumer>
    );
};
